import axios, { AxiosError, AxiosInstance } from 'axios';
import {
  LoginInputModel,
  TokenResponseModel,
  RegisterInputModel,
  CategoriesResponseModel,
  ForgotPasswordInputModel,
  ChangePasswordInputModel,
  TokenResponseErrorModel,
} from '../types/models';
import { getApiBaseUrl, getApiKey } from './utils';
import { getMemoryAccessToken, clearSession } from '../auth/authStorage';
import { refreshTokenSingleFlight } from '../auth/authService';
import { PageRoutes } from '../constants/routes';

// Re-export utilities for backward compatibility
export { getApiBaseUrl, getApiKey } from './utils';

/**
 * Apply centralized 401 error handling to an axios instance
 * Handles token refresh and retry automatically
 * @param axiosInstance - The axios instance to enhance
 */
export const apply401Interceptor = (axiosInstance: AxiosInstance) => {
  axiosInstance.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      const originalRequest = error.config as any;

      // Skip retry logic for auth endpoints to prevent infinite loops or
      // incorrectly intercepting credential errors as expired-session errors
      const isAuthEndpoint =
        originalRequest.url?.includes('/v1/account/refresh') ||
        originalRequest.url?.includes('/v1/account/token') ||
        originalRequest.url?.includes('/v1/account/logout');

      // Handle 401 Unauthorized with refresh + retry
      if (
        error.response?.status === 401 &&
        !originalRequest._retried &&
        !isAuthEndpoint
      ) {
        originalRequest._retried = true;

        try {
          console.log('Refreshing token due to 401 response...');
          // Attempt to refresh the token
          await refreshTokenSingleFlight();

          // Get the new access token
          const newToken = getMemoryAccessToken();
          if (newToken && originalRequest.headers) {
            // Update the Authorization header with the new token
            originalRequest.headers.Authorization = `Bearer ${newToken}`;

            // Retry the original request once
            return axiosInstance(originalRequest);
          }
        } catch (refreshError: any) {
          console.error('Token refresh failed, logout required:', refreshError);

          // Refresh failed - clear auth state and redirect to login
          clearSession();
          // Check current path to avoid redirect loop
          const currentPath = window.location.pathname;
          if (
            currentPath !== PageRoutes.LOGIN &&
            currentPath !== PageRoutes.SIGNUP
          ) {
            window.location.href = PageRoutes.LOGIN;
          }

          // Reject with a clear error
          return Promise.reject(refreshError);
        }
      }

      return Promise.reject(error);
    }
  );
};

const API_BASE_URL = getApiBaseUrl();
const API_KEY = getApiKey();

export const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    'x-api-key': API_KEY,
  },
});

// Add interceptors if needed
api.interceptors.request.use((config) => {
  config.headers = config.headers ?? {};
  config.headers['x-api-key'] = API_KEY;

  // Use in-memory access token from new auth system
  const token = getMemoryAccessToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Add loading interceptors
api.interceptors.request.use((config) => {
  // const event = new CustomEvent('api-loading', { detail: true });
  // window.dispatchEvent(event);
  return config;
});

api.interceptors.response.use(
  (response) => {
    const event = new CustomEvent('api-loading', { detail: false });
    window.dispatchEvent(event);
    return response;
  },
  (error) => {
    const event = new CustomEvent('api-loading', { detail: false });
    window.dispatchEvent(event);
    return Promise.reject(error);
  }
);

// Add response interceptor for better error handling and 401 refresh retry
apply401Interceptor(api);

export const loginUser = async (credentials: LoginInputModel) => {
  try {
    const response = await api.post<TokenResponseModel>(
      '/v1/account/token/check-user-s',
      credentials
    );
    return response.data;
  } catch (error) {
    console.error('Login error:', error);
    throw error;
  }
};

export const checkUserStatus = async (
  credentials: LoginInputModel
): Promise<TokenResponseModel> => {
  try {
    const response = await api.post(
      '/v1/account/check-user-status',
      credentials
    );
    return response.data;
  } catch (error) {
    // @ts-ignore
    const errorResponse = error.response.data as TokenResponseErrorModel;
    if (errorResponse?.code === 'ACCOUNT_EXISTS_OTHER_PROVIDER') {
      return {
        id: '',
        token: '',
        expires: '',
        refreshToken: '',
        signingKey: '',
        emailConfirmed: false,
        profileConfirmed: false,
        code: 'ACCOUNT_EXISTS_OTHER_PROVIDER',
      };
    } else {
      throw error;
    }
  }
};

export const getCategories = async (
  token: string
): Promise<CategoriesResponseModel> => {
  try {
    const response = await api.get<CategoriesResponseModel>(
      '/v1/user/categories',
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error('Get categories error:', error);
    throw error;
  }
};

export type RegisterCredentialsResponse = {
  id: string;
  token: string;
  accessToken: string;
  expires: string;
  expiresAtUtc: string;
  refeshToken: string;
  refreshExpiresAtUtc: string;
  emailConfirmed: boolean;
  profileConfirmed: boolean;
};

export const registerCredentials = async (credentials: {
  email: string;
  password: string;
}) => {
  try {
    const response = await api.post<TokenResponseModel>(
      '/v1/account/register-credentials',
      credentials
    );
    console.log(response);
    return response?.data;
  } catch (error) {
    console.error('Registration error:', error);
    throw error;
  }
};

export const registerUser = async (
  userData: RegisterInputModel,
  token: string
) => {
  try {
    console.log('Submitting user data:', userData);
    const response = await api.post('/v1/account/register-user', userData, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  } catch (error) {
    console.error('User registration error:', error);
    throw error;
  }
};

export const verifyOtp = async ({
  otp,
  token,
}: {
  otp: string;
  token: string;
}): Promise<boolean> => {
  try {
    const response = await api.post(
      '/v1/account/verify-otp',
      { OneTimePassword: otp },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data ?? false; // Assuming the API returns isVerified boolean
  } catch (error) {
    console.error('OTP verification error:', error);
    throw error;
  }
};

export const resendVerificationEmail = async (token: string) => {
  try {
    const response = await api.post('/v1/account/send-verify-email', null, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  } catch (error) {
    console.error('Resend verification email error:', error);
    throw error;
  }
};

export const forgotPassword = async (model: ForgotPasswordInputModel) => {
  try {
    const response = await api.post('/v1/account/forgot-password', model);
    return response.data;
  } catch (error) {
    console.error('Forgot password error:', error);
    throw error;
  }
};

export const changePassword = async (
  model: ChangePasswordInputModel,
  token: string
): Promise<boolean> => {
  try {
    const response = await api.post<ChangePasswordInputModel>(
      '/v1/account/change-password',
      model,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data as unknown as boolean;
  } catch (error) {
    console.error('Error changing password:', error);
    throw error;
  }
};
